import { useTheme, styled, Typography, Link } from "~/common/mui";
import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import { resolveUrl } from "~/utils/url/urlResolver";

export interface CMSImageHeaderMediaDTO {
  url?: string;
  altText?: string;
}

export interface CMSImageHeaderButtonDTO {
  url?: string;
  labelText?: string;
  target?: string;
}

export interface CMSImageHeaderProps {
  type: ComponentTypes.CMSImageHeader;
  id: string;
  className?: string;
  media?: CMSImageHeaderMediaDTO;
  imageLink?: string;
  headerText?: string;
  subHeaderText?: string;
  content?: string;
  button?: CMSImageHeaderButtonDTO;
  buttonVisibility?: boolean;
  mirror?: boolean;
}

export const CMSImageHeader = ({
  media,
  imageLink,
  headerText,
  subHeaderText,
  content,
  button,
  buttonVisibility = false,
  mirror = false,
  className,
}: CMSImageHeaderProps) => {
  const theme = useTheme();

  const imageUrl = media?.url;
  const altText = media?.altText ?? "";

  const hasTextOverlay = Boolean(headerText || subHeaderText || content);
  const showButton = buttonVisibility && Boolean(button?.url) && Boolean(button?.labelText);
  const linkHref = imageLink ?? undefined;

  if (!imageUrl && !hasTextOverlay) {
    return null;
  }

  const image = imageUrl ? <HeaderImage src={resolveUrl(imageUrl)} alt={altText} $mirror={mirror} /> : null;

  const textOverlay = hasTextOverlay ? (
    <Overlay>
      {headerText ? (
        <Typography variant="headlineLarge" component="h1" sx={overlayTextStyle}>
          {headerText}
        </Typography>
      ) : null}
      {subHeaderText ? (
        <Typography variant="titleMedium" component="h2" sx={overlayTextStyle}>
          {subHeaderText}
        </Typography>
      ) : null}
      {content ? (
        <Typography variant="bodyMedium" component="p" sx={overlayTextStyle}>
          {content}
        </Typography>
      ) : null}
      {showButton ? (
        <ButtonLink href={resolveUrl(button!.url!)} target={button?.target} underline="none">
          {button!.labelText}
        </ButtonLink>
      ) : null}
    </Overlay>
  ) : null;

  const banner = (
    <Root className={classNames("scha__cms", "scha__cms-image-header", className)}>
      {image}
      {textOverlay}
    </Root>
  );

  if (linkHref && !hasTextOverlay) {
    return (
      <Root className={classNames("scha__cms", "scha__cms-image-header", className)}>
        <BannerLink href={resolveUrl(linkHref)}>{image}</BannerLink>
      </Root>
    );
  }

  return banner;
};

const Root = styled("div")(({ theme }) => ({
  display: "block",
  position: "relative",
  width: "100%",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius,
}));

const HeaderImage = styled("img", {
  shouldForwardProp: (prop) => prop !== "$mirror",
})<{ $mirror: boolean }>(({ $mirror }) => ({
  display: "block",
  width: "100%",
  height: "auto",
  maxWidth: "100%",
  objectFit: "cover",
  transform: $mirror ? "scaleX(-1)" : undefined,
}));

const Overlay = styled("div")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(3),
  pointerEvents: "none",
  [theme.breakpoints.up("tablet")]: {
    padding: theme.spacing(4),
    gap: theme.spacing(1.5),
  },
}));

const overlayTextStyle = {
  color: "common.white",
  textShadow: "0 1px 3px rgba(0, 0, 0, 0.6)",
  margin: 0,
} as const;

const ButtonLink = styled(Link)(({ theme }) => ({
  pointerEvents: "auto",
  alignSelf: "flex-start",
  marginTop: theme.spacing(1),
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
}));

const BannerLink = styled("a")({ display: "block", width: "100%" });
